import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { VirtualPageList } from '../../ui/layout/VirtualPageList';
import { useIsMobile } from '../../utils/getIsMobile';
import { getWindow } from '../../utils/getWindow';

type VirtualGridProps<ItemType extends { id: string }> = {
    onLoadNext: () => void;
    items: ItemType[];
    renderItem: (item: ItemType, index: number) => ReactElement | null;
    columns?: number;
    heightOffset?: number;
    spacing?: number;
    isEndReached?: boolean;
};

const LOAD_MORE_OFFSET = 3;
const MOBILE_COLUMNS = 2;
const COLUMNS = 3;

export const VirtualGrid = <T extends { id: string }>({
    items,
    columns,
    onLoadNext,
    renderItem,
    heightOffset = 0,
    spacing = 0,
    isEndReached = false,
}: VirtualGridProps<T>) => {
    const [container, setContainer] = useState<null | HTMLDivElement>(null);
    const [key, setKey] = useState(0);
    const containerWidthRef = useRef<number>(0);
    const lastLoadIndexRef = useRef(0);
    const isMobile = useIsMobile();
    const [firstItem, setFirstItem] = useState<string | undefined>();

    if (container && container.offsetWidth !== 0) {
        containerWidthRef.current = container.offsetWidth;
    }

    // При обновлении первого айтема считаем, что изменился весь список
    // и начинаем загрузки с нуля
    useEffect(() => {
        if (firstItem !== undefined && items[0]?.id !== firstItem) {
            lastLoadIndexRef.current = 0;
        }

        items[0]?.id !== firstItem && setFirstItem(items[0]?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items[0]?.id]);

    useEffect(() => {
        let lastKeyValue = 0;

        const resizeCallback = () => {
            if (!container || container.offsetWidth === 0) {
                return;
            }

            const containerWidth = container.offsetWidth;
            containerWidthRef.current = containerWidth;

            if (containerWidth !== lastKeyValue) {
                setKey(container.offsetWidth);
                lastKeyValue = container.offsetWidth;
            }
        };

        resizeCallback();
        getWindow()?.addEventListener('resize', throttle(resizeCallback, 50));

        return () => {
            getWindow()?.removeEventListener('resize', resizeCallback);
        };
    }, [container]);

    const currentColumns = columns || (isMobile ? MOBILE_COLUMNS : COLUMNS);
    const columnWidth = Number((containerWidthRef.current / currentColumns).toFixed(2));
    const rowHeight = columnWidth + heightOffset;
    const itemsLength = Math.ceil(items.length / currentColumns);
    const loadMoreIndex = Math.ceil(items.length / currentColumns);

    return (
        <Box width="100%" ref={(node) => setContainer(node)} key={key}>
            {rowHeight && rowHeight > 0 ? (
                <VirtualPageList
                    key={items[0]?.id}
                    styles={{ width: '100%' }}
                    itemsLength={itemsLength}
                    itemHeight={rowHeight}
                    yItemStock={4}
                    renderItem={(index) => {
                        if (!items.length) {
                            return null;
                        }

                        const itemsRow = items.slice(
                            (index + 1) * currentColumns - currentColumns,
                            (index + 1) * currentColumns,
                        );

                        if (!itemsRow.length) {
                            return null;
                        }

                        if (index + LOAD_MORE_OFFSET === loadMoreIndex && !isEndReached) {
                            if (lastLoadIndexRef.current !== index) {
                                lastLoadIndexRef.current = index;
                                onLoadNext();
                            }
                        }

                        return (
                            <Flex
                                key={itemsRow[0].id}
                                height={rowHeight}
                                width={containerWidthRef.current}
                                direction="row"
                                spacing={spacing}
                            >
                                {itemsRow.map((item, columnIndex) => (
                                    <Box width={columnWidth} height={rowHeight}>
                                        {renderItem(item, index * currentColumns + columnIndex)}
                                    </Box>
                                ))}
                            </Flex>
                        );
                    }}
                />
            ) : null}
        </Box>
    );
};
